import {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
  type MouseEvent as ReactMouseEvent,
  type WheelEvent as ReactWheelEvent,
} from "react";
import {
  type LayerKey,
  type NodeColorTheme,
  type NodeDef,
  CW,
  CH,
  NW,
  NH,
  FILTER_BUTTONS,
  LEGEND_ITEMS,
  DOMAINS,
  SUBGROUPS,
  NODES,
  ARROWS,
  domainMap,
  nodeMap,
  absPos,
  centre,
  edgePt,
} from "./architecture-data";
import classes from "./ArchitectureDiagram.module.css";

/* ═══════════════════════════════════════════════
   NODE COLOR CLASS MAP
═══════════════════════════════════════════════ */

const NODE_CLASS: Record<NodeColorTheme, string> = {
  blue: classes.nodeBlue!,
  amber: classes.nodeAmber!,
  teal: classes.nodeTeal!,
  violet: classes.nodeViolet!,
  gray: classes.nodeGray!,
  green: classes.nodeGreen!,
};

const DOMAIN_CLASS: Record<string, string> = {
  domainBlue: classes.domainBlue!,
  domainAmber: classes.domainAmber!,
  domainTeal: classes.domainTeal!,
  domainViolet: classes.domainViolet!,
};

/* ═══════════════════════════════════════════════
   COMPONENT
═══════════════════════════════════════════════ */

export function ArchitectureDiagram() {
  const canvasRef = useRef<HTMLDivElement>(null);
  const [layer, setLayer] = useState<LayerKey>("all");
  const [hovered, setHovered] = useState<string | null>(null);
  const [tooltip, setTooltip] = useState<{
    show: boolean;
    x: number;
    y: number;
    title: string;
    desc: string;
  }>({ show: false, x: 0, y: 0, title: "", desc: "" });

  // Pan/zoom state
  const [transform, setTransform] = useState({ tx: 0, ty: 0, scale: 0.48 });
  const dragRef = useRef<{
    dragging: boolean;
    startX: number;
    startY: number;
    origTx: number;
    origTy: number;
  }>({ dragging: false, startX: 0, startY: 0, origTx: 0, origTy: 0 });

  // Auto-fit on mount
  useEffect(() => {
    const el = canvasRef.current;
    if (!el) return;
    const r = el.getBoundingClientRect();
    const s = Math.min((r.width - 32) / CW, (r.height - 32) / CH) * 0.97;
    setTransform({
      tx: (r.width - CW * s) / 2,
      ty: (r.height - CH * s) / 2,
      scale: s,
    });
  }, []);

  const resetView = useCallback(() => {
    const el = canvasRef.current;
    if (!el) return;
    const r = el.getBoundingClientRect();
    const s = Math.min((r.width - 32) / CW, (r.height - 32) / CH) * 0.97;
    setTransform({
      tx: (r.width - CW * s) / 2,
      ty: (r.height - CH * s) / 2,
      scale: s,
    });
  }, []);

  // Connections for hovered node
  const connectedNodes = useMemo(() => {
    if (!hovered) return null;
    const set = new Set<string>([hovered]);
    for (const a of ARROWS) {
      if (a.from === hovered) set.add(a.to);
      if (a.to === hovered) set.add(a.from);
    }
    return set;
  }, [hovered]);

  // Filtered arrows
  const visibleArrows = useMemo(() => {
    if (layer === "all") return ARROWS;
    return ARROWS.filter((a) => a.layers.includes(layer));
  }, [layer]);

  // Mouse handlers for pan
  const onCanvasMouseDown = useCallback(
    (e: ReactMouseEvent) => {
      if ((e.target as HTMLElement).closest(`.${classes.node}`)) return;
      dragRef.current = {
        dragging: true,
        startX: e.clientX,
        startY: e.clientY,
        origTx: transform.tx,
        origTy: transform.ty,
      };
    },
    [transform.tx, transform.ty],
  );

  const onCanvasMouseMove = useCallback(
    (e: ReactMouseEvent) => {
      // Tooltip position
      if (tooltip.show && canvasRef.current) {
        const r = canvasRef.current.getBoundingClientRect();
        let lx = e.clientX - r.left + 14;
        let ly = e.clientY - r.top + 14;
        if (lx + 230 > r.width) lx = e.clientX - r.left - 240;
        if (ly + 90 > r.height) ly = e.clientY - r.top - 100;
        setTooltip((prev) => ({ ...prev, x: lx, y: ly }));
      }
      // Panning
      const d = dragRef.current;
      if (!d.dragging) return;
      setTransform((prev) => ({
        ...prev,
        tx: d.origTx + (e.clientX - d.startX),
        ty: d.origTy + (e.clientY - d.startY),
      }));
    },
    [tooltip.show],
  );

  const onCanvasMouseUp = useCallback(() => {
    dragRef.current.dragging = false;
  }, []);

  // Wheel zoom
  const onWheel = useCallback((e: ReactWheelEvent) => {
    e.stopPropagation();
    const el = canvasRef.current;
    if (!el) return;
    const r = el.getBoundingClientRect();
    const mx = e.clientX - r.left;
    const my = e.clientY - r.top;
    const f = e.deltaY < 0 ? 1.1 : 0.91;
    setTransform((prev) => {
      const ns = Math.min(2.5, Math.max(0.18, prev.scale * f));
      return {
        tx: mx - (mx - prev.tx) * (ns / prev.scale),
        ty: my - (my - prev.ty) * (ns / prev.scale),
        scale: ns,
      };
    });
  }, []);

  const doZoom = useCallback((f: number) => {
    const el = canvasRef.current;
    if (!el) return;
    const r = el.getBoundingClientRect();
    const mx = r.width / 2;
    const my = r.height / 2;
    setTransform((prev) => {
      const ns = Math.min(2.5, Math.max(0.18, prev.scale * f));
      return {
        tx: mx - (mx - prev.tx) * (ns / prev.scale),
        ty: my - (my - prev.ty) * (ns / prev.scale),
        scale: ns,
      };
    });
  }, []);

  const onNodeEnter = useCallback(
    (n: NodeDef, e: ReactMouseEvent) => {
      setHovered(n.id);
      if (canvasRef.current) {
        const r = canvasRef.current.getBoundingClientRect();
        setTooltip({
          show: true,
          x: e.clientX - r.left + 14,
          y: e.clientY - r.top + 14,
          title: n.label,
          desc: n.desc,
        });
      }
    },
    [],
  );

  const onNodeLeave = useCallback(() => {
    setHovered(null);
    setTooltip((prev) => ({ ...prev, show: false }));
  }, []);

  const xformStr = `translate(${transform.tx}px, ${transform.ty}px) scale(${transform.scale})`;

  return (
    <div className={classes.wrapper} style={{ display: "flex", flexDirection: "column" }}>
      {/* Toolbar */}
      <div className={classes.toolbar}>
        <span className={classes.toolbarTitle}>Platform Architecture</span>
        <button
          className={classes.filterBtn}
          type="button"
          onClick={resetView}
        >
          Reset view
        </button>
        {FILTER_BUTTONS.map((btn) => (
          <button
            key={btn.key}
            className={layer === btn.key ? classes.filterBtnActive : classes.filterBtn}
            type="button"
            onClick={() => setLayer(btn.key)}
          >
            {btn.label}
          </button>
        ))}
        <div className={classes.legend}>
          {LEGEND_ITEMS.map((item) => (
            <span key={item.label} className={classes.legendItem}>
              <span className={classes.legendDot} style={{ background: item.color }} />
              {item.label}
            </span>
          ))}
        </div>
      </div>

      {/* Canvas */}
      <div
        ref={canvasRef}
        className={`${classes.canvas}${dragRef.current.dragging ? ` ${classes.dragging}` : ""}`}
        onMouseDown={onCanvasMouseDown}
        onMouseMove={onCanvasMouseMove}
        onMouseUp={onCanvasMouseUp}
        onMouseLeave={onCanvasMouseUp}
        onWheel={onWheel}
      >
        {/* Stage (nodes) */}
        <div
          className={classes.stage}
          style={{
            width: CW,
            height: CH,
            transform: xformStr,
          }}
        >
          {/* Domains */}
          {DOMAINS.map((d) => (
            <div
              key={d.id}
              className={`${classes.domain} ${DOMAIN_CLASS[d.cls]}`}
              style={{ left: d.x, top: d.y, width: d.w, height: d.h }}
            >
              <div className={classes.domainLabel}>
                {d.label}
              </div>
            </div>
          ))}

          {/* Subgroups */}
          {SUBGROUPS.map((sg) => {
            const dom = domainMap.get(sg.domainId)!;
            return (
              <div
                key={sg.id}
                className={classes.subgroup}
                style={{
                  left: dom.x + sg.x,
                  top: dom.y + sg.y,
                  width: sg.w,
                  height: sg.h,
                }}
              >
                <span className={classes.subgroupLabel}>{sg.label}</span>
              </div>
            );
          })}

          {/* Nodes */}
          {NODES.map((n) => {
            const pos = absPos(n);
            const isDimmed = connectedNodes && !connectedNodes.has(n.id);
            const isHighlighted = hovered === n.id;
            const Icon = n.icon;
            return (
              <div
                key={n.id}
                className={`${classes.node} ${NODE_CLASS[n.color]}${isDimmed ? ` ${classes.nodeDimmed}` : ""}${isHighlighted ? ` ${classes.nodeHighlighted}` : ""}`}
                style={{ left: pos.x, top: pos.y, width: NW, height: NH }}
                onMouseEnter={(e) => onNodeEnter(n, e)}
                onMouseLeave={onNodeLeave}
              >
                <div className={classes.nodeIcon}>
                  <Icon size={14} stroke={2} />
                </div>
                <span className={classes.nodeName}>{n.label}</span>
              </div>
            );
          })}
        </div>

        {/* Arrow SVG overlay */}
        <svg
          className={classes.arrows}
          style={{
            width: CW,
            height: CH,
            transform: xformStr,
          }}
        >
          <defs>
            <marker
              id="arch-arrowhead"
              viewBox="0 0 10 10"
              refX="9"
              refY="5"
              markerWidth="5"
              markerHeight="5"
              orient="auto-start-reverse"
            >
              <path
                d="M1.5 1.5 L8.5 5 L1.5 8.5"
                fill="none"
                stroke="context-stroke"
                strokeWidth="2"
                strokeLinecap="round"
                strokeLinejoin="round"
              />
            </marker>
          </defs>
          {visibleArrows.map((a, i) => {
            const fn = nodeMap.get(a.from);
            const tn = nodeMap.get(a.to);
            if (!fn || !tn) return null;
            const fc = centre(fn);
            const tc = centre(tn);
            const p1 = edgePt(fc.x, fc.y, tc.x, tc.y);
            const p2 = edgePt(tc.x, tc.y, fc.x, fc.y);

            const mx = (p1.x + p2.x) / 2;
            const my = (p1.y + p2.y) / 2;
            const len = Math.hypot(p2.x - p1.x, p2.y - p1.y);
            const bend = Math.min(55, len * 0.15);
            const nx = -(p2.y - p1.y) / (len || 1);
            const ny = (p2.x - p1.x) / (len || 1);
            const cx = mx + nx * bend;
            const cy = my + ny * bend;

            const isDimmed = hovered && a.from !== hovered && a.to !== hovered;
            const isHighlighted =
              hovered && (a.from === hovered || a.to === hovered);

            return (
              <path
                key={`${a.from}-${a.to}-${i}`}
                d={`M${p1.x},${p1.y} Q${cx},${cy} ${p2.x},${p2.y}`}
                className={`${classes.arrowPath}${isDimmed ? ` ${classes.arrowDimmed}` : ""}${isHighlighted ? ` ${classes.arrowHighlighted}` : ""}`}
                stroke={a.color}
              />
            );
          })}
        </svg>

        {/* Tooltip */}
        <div
          className={`${classes.tooltip}${tooltip.show ? ` ${classes.tooltipVisible}` : ""}`}
          style={{ left: tooltip.x, top: tooltip.y }}
        >
          <span className={classes.tooltipTitle}>{tooltip.title}</span>
          {tooltip.desc}
        </div>

        {/* Zoom controls */}
        <div className={classes.zoomControls}>
          <button className={classes.zoomBtn} type="button" onClick={() => doZoom(1.2)}>
            +
          </button>
          <button className={classes.zoomBtn} type="button" onClick={() => doZoom(0.83)}>
            −
          </button>
        </div>
      </div>
    </div>
  );
}
